// Dossiê Lemit em PDF — exporta a consulta aberta na Central Lemit (pessoa ou
// empresa) e, quando há quadro societário enriquecido, uma página completa por
// sócio. Substitui o fluxo de "consultar um por um e tirar print".
// jsPDF por import nomeado (e não default) para o módulo rodar também fora do
// browser — no Node o default do build CJS não é o construtor.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fileStamp } from './excelExport';
import { sanitizeNomeParaArquivo } from './utils';
import {
    LemitTipo, LemitPessoaData, LemitEmpresaData, LemitTelefone, LemitEndereco, LemitSocio,
} from '../../types';
import type { SocioEnriquecido } from '../services/lemit';
import { NoCadeia, PessoaDaCadeia, pessoasDaCadeia, ordenarCadeia, resumoCadeia } from '../services/lemitCadeia';

// ── Paleta (mesma do exportAnaliseCadastral / hero da Central Lemit) ──────────
type RGB = [number, number, number];
const DARK: RGB = [33, 41, 19];      // #212913
const OLIVE: RGB = [95, 108, 55];    // #5F6C37
const AMBER: RGB = [246, 169, 55];   // #F6A937
const INK: RGB = [30, 41, 59];
const MUTED: RGB = [122, 132, 145];
const LINE: RGB = [214, 220, 196];   // #D6DCC4
const ZEBRA: RGB = [246, 248, 240];

const M = 14;                 // margem lateral (mm)
const PAGE_W = 210;           // A4 retrato
const PAGE_H = 297;
const TOP_CONT = 22;          // topo útil das páginas 2+ (abaixo da faixa fina)
const BOTTOM = 18;            // reserva do rodapé

// ── Formatadores ─────────────────────────────────────────────────────────────
const digits = (v?: string | null) => String(v || '').replace(/\D/g, '');

const fmtDoc = (doc?: string | null): string => {
    const c = digits(doc);
    if (c.length === 11) return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (c.length === 14) return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc || '—';
};
const fmtTel = (t: LemitTelefone): string => {
    const n = digits(t.numero);
    const num = n.length >= 8 ? `${n.slice(0, n.length - 4)}-${n.slice(-4)}` : n;
    return `(${t.ddd ?? '--'}) ${num}`;
};
const fmtBRL = (v?: number | null) =>
    v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtData = (iso?: string | null): string => {
    if (!iso) return '—';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};
const fmtDataHora = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtCep = (cep?: string | null): string => {
    const d = digits(cep);
    return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : (cep || '—');
};
const fmtEndereco = (e?: LemitEndereco | null): string =>
    (e?.endereco || [e?.logradouro, e?.numero, e?.complemento].filter(Boolean).join(', ') || '—').replace(/\s+/g, ' ').trim();
const dash = (v?: string | number | null) => {
    const s = v == null ? '' : String(v).trim();
    return s || '—';
};

/** Corta o texto no limite de largura, com reticências (usado nas faixas). */
const clip = (doc: jsPDF, texto: string, larguraMm: number): string => {
    let t = String(texto || '');
    if (doc.getTextWidth(t) <= larguraMm) return t;
    while (t.length > 1 && doc.getTextWidth(`${t}…`) > larguraMm) t = t.slice(0, -1);
    return `${t}…`;
};

// ── Blocos de página ─────────────────────────────────────────────────────────

/** Faixa de capa da 1ª página; devolve o Y onde o conteúdo começa. */
function capa(doc: jsPDF, titulo: string, documento: string, chips: string[]): number {
    doc.setFillColor(...DARK);
    doc.rect(0, 0, PAGE_W, 46, 'F');
    // filete olive→âmbar (dois retângulos: jsPDF não faz gradiente)
    doc.setFillColor(...OLIVE);
    doc.rect(0, 44.6, PAGE_W * 0.72, 1.4, 'F');
    doc.setFillColor(...AMBER);
    doc.rect(PAGE_W * 0.72, 44.6, PAGE_W * 0.28, 1.4, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...AMBER);
    doc.text('C E N T R A L   L E M I T   ·   D O S S I Ê', M, 13);

    doc.setFontSize(17);
    doc.setTextColor(255, 255, 255);
    doc.text(clip(doc, titulo || '—', PAGE_W - M * 2), M, 24);

    doc.setFont('courier', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(226, 232, 240);
    doc.text(fmtDoc(documento), M, 32);

    if (chips.length) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(190, 198, 170);
        doc.text(clip(doc, chips.filter(Boolean).join('   ·   '), PAGE_W - M * 2), M, 39.5);
    }
    return 56;
}

/** Quebra de página quando não cabe `precisa` mm; devolve o Y de continuação. */
function espaco(doc: jsPDF, y: number, precisa: number): number {
    if (y + precisa <= PAGE_H - BOTTOM) return y;
    doc.addPage();
    return TOP_CONT;
}

/** Título de seção com barrinha olive; devolve o Y do conteúdo. */
function secao(doc: jsPDF, y: number, titulo: string, contagem?: number): number {
    const yy = espaco(doc, y, 18);
    doc.setFillColor(...OLIVE);
    doc.rect(M, yy - 3.4, 1.3, 4.6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(titulo.toUpperCase(), M + 3.6, yy);
    if (contagem != null) {
        const x = M + 3.6 + doc.getTextWidth(titulo.toUpperCase()) + 2.5;
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text(`(${contagem})`, x, yy);
    }
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.25);
    doc.line(M, yy + 2.6, PAGE_W - M, yy + 2.6);
    return yy + 7.5;
}

/** Tabela padrão; devolve o Y após a tabela. */
function tabela(doc: jsPDF, y: number, head: string[], body: (string | number)[][], columnStyles?: any): number {
    autoTable(doc, {
        startY: y,
        head: [head],
        body,
        theme: 'grid',
        margin: { left: M, right: M, top: TOP_CONT, bottom: BOTTOM },
        // sem isso uma linha alta fica partida entre duas páginas (nome numa, contato na outra)
        rowPageBreak: 'avoid',
        styles: {
            font: 'helvetica', fontSize: 8, textColor: INK, lineColor: LINE, lineWidth: 0.1,
            cellPadding: { top: 1.7, bottom: 1.7, left: 2.2, right: 2.2 }, overflow: 'linebreak',
        },
        headStyles: { fillColor: OLIVE, textColor: [255, 255, 255], fontSize: 7.2, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: ZEBRA },
        columnStyles,
    });
    return (doc as any).lastAutoTable.finalY + 7;
}

/** Grade rótulo/valor em 2 pares por linha (espelha os InfoCell da tela). */
function grade(doc: jsPDF, y: number, pares: [string, string][]): number {
    const linhas: (string | number)[][] = [];
    for (let i = 0; i < pares.length; i += 2) {
        const [l1, v1] = pares[i];
        const p2 = pares[i + 1];
        linhas.push([l1.toUpperCase(), v1, p2 ? p2[0].toUpperCase() : '', p2 ? p2[1] : '']);
    }
    autoTable(doc, {
        startY: y,
        body: linhas,
        theme: 'plain',
        margin: { left: M, right: M, top: TOP_CONT, bottom: BOTTOM },
        styles: {
            font: 'helvetica', fontSize: 8.5, textColor: INK, cellPadding: { top: 1.2, bottom: 1.2, left: 0, right: 3 },
            overflow: 'linebreak',
        },
        columnStyles: {
            0: { cellWidth: 32, fontSize: 6.6, fontStyle: 'bold', textColor: MUTED },
            1: { cellWidth: 59, fontStyle: 'bold' },
            2: { cellWidth: 32, fontSize: 6.6, fontStyle: 'bold', textColor: MUTED },
            3: { cellWidth: 59, fontStyle: 'bold' },
        },
    });
    return (doc as any).lastAutoTable.finalY + 7;
}

/** Aviso (usado quando um sócio não pôde ser enriquecido); quebra em várias linhas. */
function nota(doc: jsPDF, y: number, texto: string): number {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const linhas: string[] = doc.splitTextToSize(texto, PAGE_W - M * 2 - 8);
    const alturaCaixa = 5 + linhas.length * 3.9;
    const yy = espaco(doc, y, alturaCaixa + 4);
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.25);
    doc.roundedRect(M, yy - 4, PAGE_W - M * 2, alturaCaixa, 1.5, 1.5, 'FD');
    doc.setTextColor(146, 104, 24);
    linhas.forEach((linha, i) => doc.text(linha, M + 4, yy + 1.2 + i * 3.9));
    return yy + alturaCaixa + 3;
}

// ── Seções de conteúdo (compartilhadas entre dossiê principal e sócios) ──────

function contatos(doc: jsPDF, y: number, d: LemitPessoaData | LemitEmpresaData): number {
    const tels: LemitTelefone[] = [...(d.celulares || []), ...(d.fixos || [])]
        .filter((t) => t?.numero)
        .sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99));
    let yy = secao(doc, y, 'Telefones', tels.length);
    yy = tels.length
        ? tabela(doc, yy, ['Telefone', 'DDD', 'WhatsApp', 'Ranking'],
            tels.map((t) => [fmtTel(t), String(t.ddd ?? '—'), t.whatsapp ? 'Sim' : '—', t.ranking != null ? String(t.ranking) : '—']),
            { 0: { fontStyle: 'bold' }, 1: { cellWidth: 22, halign: 'center' }, 2: { cellWidth: 26, halign: 'center' }, 3: { cellWidth: 24, halign: 'center' } })
        : vazio(doc, yy, 'Nenhum telefone retornado.');

    const emails = (d.emails || []).filter((e) => e?.email);
    yy = secao(doc, yy, 'E-mails', emails.length);
    yy = emails.length
        ? tabela(doc, yy, ['E-mail', 'Ranking'],
            emails.map((e) => [String(e.email), e.ranking != null ? String(e.ranking) : '—']),
            { 1: { cellWidth: 24, halign: 'center' } })
        : vazio(doc, yy, 'Nenhum e-mail retornado.');
    return yy;
}

function enderecos(doc: jsPDF, y: number, lista: LemitEndereco[]): number {
    let yy = secao(doc, y, 'Endereços', lista.length);
    if (!lista.length) return vazio(doc, yy, 'Nenhum endereço retornado.');
    return tabela(doc, yy, ['Endereço', 'Bairro', 'Cidade/UF', 'CEP'],
        lista.map((e) => [
            fmtEndereco(e), dash(e?.bairro),
            [e?.cidade, e?.uf].filter(Boolean).join('/') || '—',
            fmtCep(e?.cep),
        ]),
        { 0: { cellWidth: 74 }, 3: { cellWidth: 22 } });
}

function veiculos(doc: jsPDF, y: number, d: LemitPessoaData | LemitEmpresaData): number {
    const carros = d.carros || [];
    if (!carros.length) return y;
    const yy = secao(doc, y, 'Veículos', carros.length);
    return tabela(doc, yy, ['Placa', 'Marca / Modelo', 'Ano', 'Renavam'],
        carros.map((c) => [
            dash(c.placa), dash(c.marca),
            [c.ano_fabricacao, c.ano_modelo].filter(Boolean).join('/') || '—',
            dash(c.renavan),
        ]),
        { 0: { cellWidth: 24, fontStyle: 'bold' }, 2: { cellWidth: 22, halign: 'center' }, 3: { cellWidth: 32 } });
}

function vazio(doc: jsPDF, y: number, texto: string): number {
    const yy = espaco(doc, y, 10);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(texto, M, yy);
    return yy + 9;
}

/** Bloco completo de uma pessoa física. */
function blocoPessoa(doc: jsPDF, y: number, d: LemitPessoaData): number {
    let yy = secao(doc, y, 'Dados cadastrais');
    yy = grade(doc, yy, [
        ['Nome', dash(d.nome)],
        ['CPF', fmtDoc(d.cpf)],
        ['Nascimento', fmtData(d.data_nascimento)],
        ['Sexo', dash(d.sexo)],
        ['Nome da mãe', dash(d.nome_mae)],
        ['Situação do CPF', dash(d.situacao_cpf)],
        ['Renda estimada', d.renda != null ? fmtBRL(d.renda) : '—'],
        ['Ocupação', dash(d.ocupacao)],
        ['Score de crédito', dash(d.risco_credito?.score_credito)],
        ['Consta óbito', d.falecido ? 'SIM' : 'Não'],
    ]);
    yy = contatos(doc, yy, d);
    yy = enderecos(doc, yy, (d.enderecos || []).filter(Boolean).sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99)));
    yy = veiculos(doc, yy, d);

    if (d.vinculos?.length) {
        yy = secao(doc, yy, 'Vínculos familiares', d.vinculos.length);
        yy = tabela(doc, yy, ['Nome', 'CPF', 'Vínculo'],
            d.vinculos.map((v) => [dash(v.nome_vinculo), fmtDoc(v.cpf_vinculo), dash(v.tipo_vinculo)]),
            { 1: { cellWidth: 36 }, 2: { cellWidth: 40 } });
    }
    if (d.participacao_societaria?.length) {
        yy = secao(doc, yy, 'Participações societárias', d.participacao_societaria.length);
        yy = tabela(doc, yy, ['CNPJ', 'Empresa', 'Participação', 'Situação'],
            d.participacao_societaria.map((p) => [
                fmtDoc(p.cnpj), dash(p.nome),
                p.participacao_socio != null ? `${p.participacao_socio}%` : '—',
                dash(p.situacao_cadastral),
            ]),
            { 0: { cellWidth: 38 }, 2: { cellWidth: 26, halign: 'center' }, 3: { cellWidth: 28 } });
    }
    return yy;
}

/**
 * Quadro societário de uma empresa. `porDoc` traz o que já se sabe de cada sócio
 * (contato + se ele tem página própria adiante no dossiê).
 */
function quadroSocietario(
    doc: jsPDF, y: number, socios: LemitSocio[], porDoc?: Map<string, NoCadeia>,
): number {
    let yy = secao(doc, y, 'Quadro societário', socios.length);
    if (!socios.length) return vazio(doc, yy, 'Nenhum sócio retornado.');
    return tabela(doc, yy,
        ['Nome', 'CPF/CNPJ', 'Part.', 'Melhor telefone', 'Melhor e-mail'],
        socios.map((s) => {
            const enr = porDoc?.get(digits(s.cpf));
            return [
                dash(s.nome),
                fmtDoc(s.cpf),
                s.participacao != null ? `${s.participacao}%` : '—',
                enr?.melhorTelefone || (enr ? statusCurto(enr) : 'não enriquecido'),
                enr?.melhorEmail || '—',
            ];
        }),
        {
            0: { cellWidth: 52, fontStyle: 'bold' }, 1: { cellWidth: 32 },
            2: { cellWidth: 14, halign: 'center' }, 3: { cellWidth: 30 },
        });
}

/** Bloco completo de uma empresa. O quadro societário só sai quando pedido. */
function blocoEmpresa(
    doc: jsPDF, y: number, d: LemitEmpresaData,
    quadro?: { porDoc?: Map<string, NoCadeia> },
): number {
    let yy = secao(doc, y, 'Dados cadastrais');
    yy = grade(doc, yy, [
        ['Razão social', dash(d.razao_social)],
        ['Nome fantasia', dash(d.nome_fantasia)],
        ['CNPJ', fmtDoc(d.cnpj)],
        ['Situação', dash(d.situacao)],
        ['Tipo', dash(d.tipo)],
        ['Fundação', fmtData(d.data_fundacao)],
        ['CNAE', `${d.cnae?.numero || ''} ${d.cnae?.descricao || ''}`.trim() || '—'],
        ['Segmento CNAE', dash(d.cnae?.segmento)],
    ]);
    yy = contatos(doc, yy, d);
    yy = enderecos(doc, yy, d.endereco ? [d.endereco] : []);
    yy = veiculos(doc, yy, d);
    // Sem isso a página de um sócio-empresa terminava sem dizer de quem ela é —
    // era exatamente o buraco da cadeia: CNPJ atrás de CNPJ, sem nenhum CPF.
    if (quadro) yy = quadroSocietario(doc, yy, d.socios || [], quadro.porDoc);
    return yy;
}

// ── Cadeia societária ────────────────────────────────────────────────────────

/** O que o dossiê tem daquele nó (é o que diz se valeu a pena descer mais). */
function statusNo(n: NoCadeia): string {
    if (n.repetido) return 'já detalhado acima';
    if (!n.data) return statusCurto(n);
    if (n.tipo === 'empresa') {
        const q = ((n.data as LemitEmpresaData).socios || []).length;
        return q ? `dossiê · ${q} sócio(s)` : 'dossiê · sem sócios';
    }
    return 'dossiê completo';
}

/** Árvore da cadeia: a hierarquia inteira em uma tabela, indentada por nível. */
function arvoreCadeia(doc: jsPDF, y: number, nos: NoCadeia[], nota?: string | null): number {
    const r = resumoCadeia(nos);
    let yy = secao(doc, y, 'Cadeia societária', nos.length);
    yy -= 2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    doc.setTextColor(...MUTED);
    doc.text(
        `${r.empresas} empresa(s) · ${r.pessoas} pessoa(s) física(s) · ${r.nivelMax} ${r.nivelMax === 1 ? 'nível' : 'níveis'} de profundidade`,
        M, yy);
    yy += 5;
    if (nota) {
        doc.text(clip(doc, nota, PAGE_W - M * 2), M, yy - 0.6);
        yy += 4.4;
    }
    return tabela(doc, yy,
        ['Estrutura', 'CPF/CNPJ', 'Part.', 'Tipo', 'Dossiê'],
        nos.map((n) => [
            // '»' em vez de '└': fonte padrão do jsPDF (WinAnsi) não tem box-drawing
            `${'     '.repeat(Math.max(n.nivel - 1, 0))}${n.nivel > 1 ? '» ' : ''}${n.nome || fmtDoc(n.cpf)}`,
            fmtDoc(n.cpf),
            n.participacao != null ? `${n.participacao}%` : '—',
            n.tipo === 'empresa' ? 'PJ' : 'PF',
            statusNo(n),
        ]),
        {
            0: { cellWidth: 68, fontStyle: 'bold' }, 1: { cellWidth: 32 },
            2: { cellWidth: 14, halign: 'center' }, 3: { cellWidth: 12, halign: 'center' },
        });
}

/** O achado da expansão: todos os CPFs no fim da cadeia, com contato e origem. */
function listaPessoas(doc: jsPDF, y: number, pessoas: PessoaDaCadeia[]): number {
    let yy = secao(doc, y, 'Pessoas físicas na cadeia', pessoas.length);
    if (!pessoas.length) {
        return vazio(doc, yy, 'Nenhum CPF alcançado — a cadeia parou antes de chegar a pessoa física.');
    }
    return tabela(doc, yy,
        ['Nome', 'CPF', 'Telefone', 'E-mail', 'Entra por'],
        pessoas.map((p) => [
            dash(p.nome), fmtDoc(p.cpf),
            p.melhorTelefone || (p.status === 'ok' ? 'sem contato' : statusCurto({ status: p.status } as SocioEnriquecido)),
            p.melhorEmail || '—',
            p.vias.join(' · ') || '—',
        ]),
        {
            0: { cellWidth: 44, fontStyle: 'bold' }, 1: { cellWidth: 30 },
            2: { cellWidth: 26 }, 3: { cellWidth: 38 },
        });
}

/** Faixas fixas: cabeçalho das páginas 2+ e rodapé de todas. */
function acabamento(doc: jsPDF, titulo: string, rodape: string): void {
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        if (i > 1) {
            doc.setFillColor(...DARK);
            doc.rect(0, 0, PAGE_W, 12, 'F');
            doc.setFillColor(...AMBER);
            doc.rect(0, 12, PAGE_W, 0.6, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.setTextColor(255, 255, 255);
            doc.text(clip(doc, titulo, 120), M, 7.8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(190, 198, 170);
            doc.text('Dossiê Lemit · VigorHub', PAGE_W - M, 7.8, { align: 'right' });
        }
        doc.setDrawColor(...LINE);
        doc.setLineWidth(0.25);
        doc.line(M, PAGE_H - 12.5, PAGE_W - M, PAGE_H - 12.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.8);
        doc.setTextColor(...MUTED);
        doc.text(clip(doc, rodape, PAGE_W - M * 2 - 16), M, PAGE_H - 8.5);
        doc.setFont('helvetica', 'bold');
        doc.text(`${i}/${total}`, PAGE_W - M, PAGE_H - 8.5, { align: 'right' });
    }
}

// ── API pública ──────────────────────────────────────────────────────────────

export interface DossiePdfParams {
    tipo: LemitTipo;
    data: LemitPessoaData | LemitEmpresaData;
    /** Sócios diretos já enriquecidos (empresa) — cada um vira uma página do dossiê. */
    socios?: SocioEnriquecido[];
    /**
     * Cadeia societária expandida recursivamente (sócios de sócios). Quando vem
     * preenchida substitui `socios`: o dossiê ganha a árvore, a lista consolidada
     * de CPFs e uma página por nó, em profundidade.
     */
    cadeia?: NoCadeia[];
    /** Texto do porquê a expansão parou (profundidade, teto, cancelamento…). */
    notaCadeia?: string | null;
    geradoPor?: string | null;
    /** Quando a consulta principal foi feita (para o leitor saber a validade do dado). */
    consultadoEm?: string | null;
}

/** Monta e baixa o PDF do dossiê aberto na Central Lemit. */
export function exportarDossieLemitPdf(p: DossiePdfParams): void {
    const { doc, nomeArquivo } = construirDossieLemitPdf(p);
    doc.save(nomeArquivo);
}

/** Monta o documento sem baixar (separado do save para ser testável fora do browser). */
export function construirDossieLemitPdf(p: DossiePdfParams): { doc: jsPDF; nomeArquivo: string } {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const empresa = p.tipo === 'empresa' ? (p.data as LemitEmpresaData) : null;
    const pessoa = p.tipo === 'pessoa' ? (p.data as LemitPessoaData) : null;

    const nome = (empresa ? (empresa.razao_social || empresa.nome_fantasia) : pessoa?.nome) || 'Sem nome';
    const documento = empresa ? empresa.cnpj : pessoa?.cpf;
    const raizDoc = digits(documento);

    // Cadeia recursiva quando houver; senão os sócios diretos viram nós de nível 1,
    // para o resto do dossiê ser montado por um caminho só.
    const usouCadeia = !!p.cadeia?.length;
    const nos: NoCadeia[] = usouCadeia
        ? ordenarCadeia(p.cadeia as NoCadeia[], raizDoc)
        : (p.socios || []).filter(Boolean).map((s) => ({
            ...s, nivel: 1, paiDoc: raizDoc, paiNome: nome, repetido: false,
        }));

    const porDoc = new Map<string, NoCadeia>();
    nos.forEach((n) => {
        const atual = porDoc.get(n.cpf);
        if (!atual || (!atual.data && n.data)) porDoc.set(n.cpf, n);
    });

    // Uma página por documento com dossiê (repetido não repagina)
    const paginados: NoCadeia[] = [];
    const jaPaginado = new Set<string>();
    nos.forEach((n) => {
        if (!n.data || n.repetido || jaPaginado.has(n.cpf)) return;
        jaPaginado.add(n.cpf);
        paginados.push(n);
    });

    let y = capa(doc, nome, documento || '', [
        empresa ? 'Pessoa Jurídica' : 'Pessoa Física',
        (empresa ? empresa.situacao : pessoa?.situacao_cpf) || '',
        p.consultadoEm ? `Consulta de ${fmtDataHora(p.consultadoEm)}` : '',
    ]);

    y = empresa ? blocoEmpresa(doc, y, empresa) : blocoPessoa(doc, y, pessoa as LemitPessoaData);

    // Quadro societário direto da raiz (o da cadeia inteira vem logo abaixo)
    if (empresa?.socios?.length) {
        y = quadroSocietario(doc, y, empresa.socios, porDoc);
        const diretos = nos.filter((n) => n.nivel === 1 && !n.repetido);
        const semDados = diretos.filter((s) => !s.data);
        if (semDados.length) {
            y = nota(doc, y, `${semDados.length} sócio(s) sem dossiê individual: ${semDados.map((s) => `${s.nome || fmtDoc(s.cpf)} (${statusCurto(s)})`).join('; ')}`);
        }
    }

    // Cadeia societária + os CPFs no fim dela (só quando houve expansão recursiva)
    if (usouCadeia) {
        y = arvoreCadeia(doc, y, nos, p.notaCadeia);
        y = listaPessoas(doc, y, pessoasDaCadeia(nos));
    }

    // Uma página por documento enriquecido
    paginados.forEach((s, i) => {
        doc.addPage();
        let ys = TOP_CONT + 4;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...OLIVE);
        const trilha = usouCadeia
            ? `NÍVEL ${s.nivel}   ·   VIA ${(s.paiNome || fmtDoc(s.paiDoc)).toUpperCase()}`
            : nome.toUpperCase();
        doc.text(clip(doc, `${usouCadeia ? 'CADEIA' : 'SÓCIO'} ${i + 1} DE ${paginados.length}   ·   ${trilha}`, PAGE_W - M * 2), M, ys);
        ys += 6;
        doc.setFontSize(14);
        doc.setTextColor(...INK);
        doc.text(clip(doc, s.nome || fmtDoc(s.cpf), PAGE_W - M * 2), M, ys);
        ys += 5.5;
        doc.setFont('courier', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...INK);
        doc.text(fmtDoc(s.cpf), M, ys);
        const xDepoisDoDoc = M + doc.getTextWidth(fmtDoc(s.cpf)) + 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text([
            s.participacao != null ? `${s.participacao}% de participação` : null,
            s.source === 'cache' ? 'dado já consultado antes' : 'consulta nova',
        ].filter(Boolean).join('   ·   '), xDepoisDoDoc, ys);
        ys += 8;
        y = s.tipo === 'empresa'
            ? blocoEmpresa(doc, ys, s.data as LemitEmpresaData, { porDoc })
            : blocoPessoa(doc, ys, s.data as LemitPessoaData);
    });

    const res = usouCadeia ? resumoCadeia(nos) : null;
    const carimbo = [
        'Confidencial · uso interno VigorHub — contém dados pessoais (LGPD)',
        p.geradoPor ? `gerado por ${p.geradoPor}` : null,
        `em ${new Date().toLocaleString('pt-BR')}`,
        res
            ? `cadeia: ${res.empresas} empresa(s), ${res.pessoas} CPF(s), ${res.nivelMax} ${res.nivelMax === 1 ? 'nível' : 'níveis'}`
            : (paginados.length ? `${paginados.length} sócio(s) no dossiê` : null),
    ].filter(Boolean).join(' · ');
    acabamento(doc, `${nome} · ${fmtDoc(documento)}`, carimbo);

    const slug = sanitizeNomeParaArquivo(nome).replace(/\s+/g, '-').toLowerCase().slice(0, 40) || 'dossie';
    return { doc, nomeArquivo: `dossie-lemit_${slug}_${digits(documento)}_${fileStamp()}.pdf` };
}

/** Rótulo curto do porquê de um sócio não ter dossiê. */
function statusCurto(s: SocioEnriquecido): string {
    if (s.status === 'ok') return 'sem contato';
    if (s.status === 'sem_documento') return 'sem CPF/CNPJ';
    if (s.status === 'nao_encontrado') return 'não encontrado';
    return 'falhou';
}
